import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { jwtDecode, JwtPayload } from "jwt-decode";
import ApiConstants from "../constants/api_constants";
import { AuthenticationLocalService } from "../../features/authentication/data/data_source/local/authentication_local_service";
import { AuthenticationApiService } from "../../features/authentication/data/data_source/remote/authentication_api_service";
import { markForcedLogout } from "../utils/forced_logout";

function errorCode(error: unknown): string | undefined {
    return (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
}

declare module 'axios' {
    interface AxiosRequestConfig {
      requiresToken?: boolean;
    }
  }

const axiosInstance = axios.create({
    baseURL: ApiConstants().apiBaseUrl,
});

function isTokenValid(token: string){
    let result = false;
    const decoded = jwtDecode<JwtPayload>(token);
    const currentDate = new Date();

    // JWT exp is in seconds
    if (decoded.exp == null || decoded.exp * 1000 < currentDate.getTime()) {
        // Missing or past expiration: token is invalid, keep result = false
    } else {
        result = true;
    }
    return result;
}

axiosInstance.interceptors.request.use(
    async (config) => {
      if (config.requiresToken === false) return config;
      const authLocalService = new AuthenticationLocalService();
      const token = authLocalService.getAccessToken();
      
      if (token != null){
        const isValid = isTokenValid(token);
        if (!isValid){
          const refreshToken = authLocalService.getRefreshToken();
          if (refreshToken != null) {
            const authRemoteService = new AuthenticationApiService();
            try {
                const response = await authRemoteService.refreshToken(refreshToken);
                if (response.status == 200){
                    authLocalService.setAccessToken(response.data.accessToken)
                    authLocalService.setRefreshToken(response.data.refreshToken)
                }
            } catch (error) {
                if (typeof window !== "undefined") {
                  markForcedLogout(errorCode(error));
                  authLocalService.removeAccessToken();
                  authLocalService.removeRefreshToken();
                  window.location.href = "/welcome";
                }
            }
          }
        }
    }
      
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      return config;
    },
    (error) => Promise.reject(error)
  );

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (typeof window !== "undefined") {
      if (error.response?.status === 401) {
        const authLocalService = new AuthenticationLocalService();
        // A 401 while signed in means the API revoked the session (archived
        // organization, ban, password change...): tell the user on /welcome.
        if (authLocalService.getAccessToken() != null) {
          markForcedLogout(errorCode(error));
        }
        authLocalService.removeAccessToken();
        authLocalService.removeRefreshToken();
        window.location.href = "/welcome";
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Request function with automatic token management
 * @param config Request configuration
 * @param requiresToken Indicates if the request requires authentication (default: true)
 */
export const request = <T = unknown>(
    config: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> => {
    return axiosInstance({
      ...config,
      requiresToken: config.requiresToken !== false,
      headers: {
        ...config.headers,
      }
    });
  };